import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, FileText, IndianRupee, Receipt, Link, Upload, Trash2, ExternalLink, Save, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PODetail = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { user, hasTabAccess } = useAuth();
  const canEditPO = hasTabAccess('purchase_orders') || user?.is_super_admin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [po, setPo] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [notes, setNotes] = useState('');
  const [emailLinks, setEmailLinks] = useState([]);
  const [newEmailLink, setNewEmailLink] = useState('');
  const [status, setStatus] = useState('draft');

  useEffect(() => {
    fetchPODetails();
  }, [poId]);

  const fetchPODetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/purchase-orders/${poId}`, {
        withCredentials: true,
      });
      setPo(response.data);
      setRemarks(response.data.remarks || '');
      setNotes(response.data.notes || '');
      setEmailLinks(response.data.email_links || []);
      setStatus(response.data.status || 'draft');
    } catch (error) {
      toast.error('Failed to load purchase order details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/purchase-orders/${poId}/export-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/purchase-orders/${poId}/details`,
        null,
        {
          params: { remarks, email_links: emailLinks, status, notes },
          withCredentials: true,
        }
      );
      toast.success('Details saved successfully');
      fetchPODetails();
    } catch (error) {
      toast.error('Failed to save details');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmailLink = () => {
    if (newEmailLink.trim()) {
      setEmailLinks([...emailLinks, newEmailLink.trim()]);
      setNewEmailLink('');
    }
  };

  const handleRemoveEmailLink = (index) => {
    setEmailLinks(emailLinks.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/purchase-orders/${poId}/attachments`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded successfully');
      fetchPODetails();
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = async (filename) => {
    if (!window.confirm('Delete this attachment?')) return;

    try {
      await axios.delete(`${API}/purchase-orders/${poId}/attachments/${encodeURIComponent(filename)}`, {
        withCredentials: true,
      });
      toast.success('Attachment deleted');
      fetchPODetails();
    } catch (error) {
      toast.error('Failed to delete attachment');
      console.error(error);
    }
  };

  const downloadAttachment = async (attachment) => {
    // Attachments now live in object storage — see InvoiceDetail.js note.
    try {
      const res = await axios.get(
        `${API}/purchase-orders/${po.id}/attachments/${encodeURIComponent(attachment.filename)}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const blob = new Blob([res.data], { type: attachment.content_type || res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download attachment');
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Purchase order not found</p>
        <Button className="mt-4" onClick={() => navigate('/purchase-orders')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to POs
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="po-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/purchase-orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
              {po.po_number}
              {(() => {
                const s = (status || 'draft').toLowerCase();
                const variantMap = {
                  draft: 'secondary',
                  approved: 'default',
                  final: 'default',
                  paid: 'success',
                  cancelled: 'destructive',
                };
                const labelMap = {
                  draft: 'Draft',
                  approved: 'Approved',
                  final: 'Final',
                  paid: 'Paid',
                  cancelled: 'Cancelled',
                };
                return (
                  <Badge variant={variantMap[s] || 'secondary'}>
                    {labelMap[s] || status}
                  </Badge>
                );
              })()}
            </h1>
            <p className="text-muted-foreground">{po.vendor_name}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          {canEditPO && (
            <Button onClick={handleSaveDetails} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* PO Information */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Purchase Order Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">PO Number</p>
                  <p className="font-semibold">{po.po_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-semibold">{new Date(po.date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-semibold">{po.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-semibold">{po.department || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Budget Head</p>
                  <p className="font-semibold">{po.budget_head || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tournament</p>
                  <p className="font-semibold">{po.fiscal_year || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Details */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Taxable Value</p>
                  <p className="text-xl font-bold">₹{po.taxable_value?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Net Payable</p>
                  <p className="text-xl font-bold text-primary">₹{po.net_payable?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Invoiced</p>
                  <p className="text-xl font-bold text-green-600">₹{po.total_invoiced?.toLocaleString('en-IN') || 0}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Open Value</p>
                  <p className="text-xl font-bold text-orange-600">₹{po.open_value?.toLocaleString('en-IN') || 0}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">CGST:</span> ₹{po.cgst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">SGST:</span> ₹{po.sgst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">IGST:</span> ₹{po.igst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">TDS:</span> ₹{po.tds?.toLocaleString('en-IN') || 0}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Invoices */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Related Invoices ({po.invoices?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {po.invoices?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No invoices linked to this PO</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 text-xs uppercase text-muted-foreground">
                        <th className="text-left py-2 px-3">Invoice #</th>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-right py-2 px-3">Taxable Value</th>
                        <th className="text-right py-2 px-3">Net Payable</th>
                        <th className="text-center py-2 px-3">Status</th>
                        <th className="text-center py-2 px-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {po.invoices?.map((inv) => (
                        <tr key={inv.id} className="border-b border-border hover:bg-muted/30">
                          <td className="py-2 px-3 font-mono text-sm">{inv.invoice_number}</td>
                          <td className="py-2 px-3 text-sm">{new Date(inv.date).toLocaleDateString()}</td>
                          <td className="py-2 px-3 text-right">₹{inv.taxable_value?.toLocaleString('en-IN')}</td>
                          <td className="py-2 px-3 text-right font-semibold">₹{inv.net_payable?.toLocaleString('en-IN')}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant={
                              inv.status === 'paid' ? 'success'
                              : inv.status === 'cancelled' ? 'destructive'
                              : (inv.status === 'final' || inv.status === 'approved') ? 'default'
                              : 'secondary'
                            }>
                              {(inv.status || 'draft').replace(/\b\w/g, c => c.toUpperCase())}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/invoices/${inv.id}`)}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={status} onValueChange={setStatus} disabled={!canEditPO}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Remarks */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Add remarks or notes..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                disabled={!canEditPO}
              />
            </CardContent>
          </Card>

          {/* Email Links */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                Email Links
              </CardTitle>
              <CardDescription>Link related email threads</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {emailLinks.map((link, index) => (
                <div key={`${link}-${index}`} className="flex items-center gap-2">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-sm text-primary hover:underline truncate"
                  >
                    {link}
                  </a>
                  {canEditPO && (
                    <Button size="icon" variant="ghost" onClick={() => handleRemoveEmailLink(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {canEditPO && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste email link..."
                    value={newEmailLink}
                    onChange={(e) => setNewEmailLink(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddEmailLink()}
                  />
                  <Button size="icon" onClick={handleAddEmailLink}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Attachments
              </CardTitle>
              <CardDescription>Upload proof documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {po.attachments?.map((att, index) => (
                <div key={att.id || `${att.filename}-${index}`} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <button
                    onClick={() => downloadAttachment(att)}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {att.filename}
                  </button>
                  {canEditPO && (
                    <Button size="icon" variant="ghost" onClick={() => handleDeleteAttachment(att.filename)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {canEditPO && (
                <div>
                  <input
                    type="file"
                    id="po-attachment"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="po-attachment">
                    <Button variant="outline" className="w-full cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload File'}
                      </span>
                    </Button>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes — editable. Used as the line-item description in the PDF. */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
              <CardDescription>
                Shown as the item description on the printed PO. AI fills bullets from the
                Proforma Invoice — feel free to edit before exporting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Item description / PI terms (bullets supported, one per line)…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={8}
                disabled={!canEditPO}
                className="font-mono text-sm whitespace-pre-wrap"
                data-testid="po-notes-input"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PODetail;
